import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronRight,
  ChevronDown,
  Calendar,
  ZoomIn,
  ZoomOut,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ProjectItemDialog from './ProjectItemDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  todo: '#a1a1aa', // zinc
  in_progress: '#3b82f6', // blue
  review: '#f59e0b', // amber
  blocked: '#ef4444', // red
  done: '#10b981', // emerald
};

const STATUS_LABELS = {
  todo: 'Yeni',
  in_progress: 'İcra olunur',
  review: 'Təhvilə hazırdır',
  blocked: 'Problemli',
  done: 'Təhvil alındı',
};

const DAY_MS = 24 * 60 * 60 * 1000;
const ROW_H = 36;
const HEADER_H = 56;

function dateOnly(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function diffDays(a, b) {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function formatDay(d) {
  return d.toLocaleDateString('az-AZ', { day: '2-digit', month: 'short' });
}

function formatMonth(d) {
  return d.toLocaleDateString('az-AZ', { month: 'long', year: 'numeric' });
}

function isWeekend(d) {
  const day = d.getDay();
  return day === 0 || day === 6;
}

// Build tree: root items (no parent_id or parent missing) then children
function buildTree(items) {
  const byId = new Map(items.map((i) => [i.id, i]));
  const roots = [];
  const childrenOf = new Map();
  items.forEach((it) => {
    if (it.parent_id && byId.has(it.parent_id)) {
      const arr = childrenOf.get(it.parent_id) || [];
      arr.push(it);
      childrenOf.set(it.parent_id, arr);
    } else {
      roots.push(it);
    }
  });
  // Sort children & roots by start_date (nulls last), then title
  const sorter = (a, b) => {
    const ad = a.start_date ? new Date(a.start_date).getTime() : Infinity;
    const bd = b.start_date ? new Date(b.start_date).getTime() : Infinity;
    if (ad !== bd) return ad - bd;
    return (a.title || '').localeCompare(b.title || '');
  };
  roots.sort(sorter);
  childrenOf.forEach((arr) => arr.sort(sorter));
  return { roots, childrenOf };
}

function flattenTree(roots, childrenOf, collapsed) {
  const rows = [];
  const walk = (items, depth) => {
    items.forEach((it) => {
      const children = childrenOf.get(it.id) || [];
      const hasChildren = children.length > 0;
      const isCollapsed = collapsed.has(it.id);
      rows.push({ item: it, depth, hasChildren, isCollapsed });
      if (hasChildren && !isCollapsed) walk(children, depth + 1);
    });
  };
  walk(roots, 0);
  return rows;
}

// ==================== Main ====================
export default function ProjectGanttTab({ project, members, canEdit, onItemsChanged }) {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set());
  const [dayWidth, setDayWidth] = useState(36);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const scrollRef = useRef(null);

  const fetchItems = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${project.id}/items`, { headers });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mərhələlər yüklənmədi');
    } finally {
      setLoading(false);
    }
  }, [project?.id, headers]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSave = async (form) => {
    const payload = {
      ...form,
      start_date: form.start_date || null,
      due_date: form.due_date || null,
      parent_id: form.parent_id || null,
    };
    try {
      if (editingItem?.id) {
        await axios.put(`${API}/projects/${project.id}/items/${editingItem.id}`, payload, { headers });
        toast.success('Yeniləndi');
      } else {
        await axios.post(`${API}/projects/${project.id}/items`, payload, { headers });
        toast.success('Mərhələ əlavə edildi');
      }
      setDialogOpen(false);
      setEditingItem(null);
      fetchItems();
      onItemsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Saxlanılmadı');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.title}" silinsin?`)) return;
    try {
      await axios.delete(`${API}/projects/${project.id}/items/${item.id}`, { headers });
      toast.success('Silindi');
      setDialogOpen(false);
      setEditingItem(null);
      fetchItems();
      onItemsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Silinmədi');
    }
  };

  const tree = useMemo(() => buildTree(items), [items]);
  const rows = useMemo(
    () => flattenTree(tree.roots, tree.childrenOf, collapsed),
    [tree, collapsed]
  );

  // Items with at least one date (we render only these on the timeline)
  const datedItems = useMemo(
    () => items.filter((i) => i.start_date || i.due_date),
    [items]
  );

  // Range computation
  const { rangeStart, rangeEnd, totalDays } = useMemo(() => {
    if (datedItems.length === 0) {
      const today = dateOnly(new Date().toISOString());
      return {
        rangeStart: addDays(today, -7),
        rangeEnd: addDays(today, 21),
        totalDays: 28,
      };
    }
    let min = Infinity;
    let max = -Infinity;
    datedItems.forEach((it) => {
      const s = it.start_date ? dateOnly(it.start_date) : null;
      const d = it.due_date ? dateOnly(it.due_date) : null;
      const a = s ? s.getTime() : d?.getTime();
      const b = d ? d.getTime() : s?.getTime();
      if (a && a < min) min = a;
      if (b && b > max) max = b;
    });
    const start = addDays(new Date(min), -3);
    const end = addDays(new Date(max), 5);
    return {
      rangeStart: start,
      rangeEnd: end,
      totalDays: Math.max(1, diffDays(start, end) + 1),
    };
  }, [datedItems]);

  // Build month spans for header (first row)
  const monthSpans = useMemo(() => {
    const spans = [];
    let cursor = new Date(rangeStart);
    while (cursor <= rangeEnd) {
      const monthStart = new Date(cursor);
      const lastDayOfMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
      const endOfSpan = lastDayOfMonth > rangeEnd ? rangeEnd : lastDayOfMonth;
      const days = diffDays(monthStart, endOfSpan) + 1;
      spans.push({ key: monthStart.toISOString(), label: formatMonth(monthStart), days });
      cursor = addDays(endOfSpan, 1);
    }
    return spans;
  }, [rangeStart, rangeEnd]);

  const dayList = useMemo(() => {
    const arr = [];
    for (let i = 0; i < totalDays; i++) arr.push(addDays(rangeStart, i));
    return arr;
  }, [rangeStart, totalDays]);

  const today = dateOnly(new Date().toISOString());
  const todayOffset = diffDays(rangeStart, today);
  const showTodayLine = todayOffset >= 0 && todayOffset < totalDays;

  const toggleCollapse = (id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBarClick = (item) => {
    setEditingItem(item);
    setDialogOpen(true);
  };

  const scrollToToday = () => {
    if (!scrollRef.current || !showTodayLine) return;
    const left = Math.max(0, todayOffset * dayWidth - 240);
    scrollRef.current.scrollTo({ left, behavior: 'smooth' });
  };

  useEffect(() => {
    // Auto-scroll to today on first load
    if (rows.length > 0) {
      setTimeout(scrollToToday, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, dayWidth]);

  const timelineWidth = totalDays * dayWidth;
  const LEFT_COL_W = 280;

  if (loading) {
    return <div className="text-sm text-zinc-500 py-4" data-testid="gantt-loading">Yüklənir...</div>;
  }

  if (items.length === 0) {
    return (
      <div className="border border-dashed rounded-lg py-12 text-center text-zinc-500" data-testid="gantt-empty">
        <Calendar className="h-10 w-10 mx-auto mb-3 text-zinc-300" />
        <div className="text-sm">Hələ mərhələ yoxdur</div>
        <div className="text-xs text-zinc-400 mt-1">
          Gantt cədvəlində görmək üçün mərhələlər əlavə edin (start_date və due_date ilə)
        </div>
      </div>
    );
  }

  return (
    <div data-testid="project-gantt-tab" className="border rounded-lg bg-white overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-zinc-50">
        <div className="text-xs text-zinc-600">
          {datedItems.length} / {items.length} mərhələ • {formatDay(rangeStart)} → {formatDay(rangeEnd)}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setDayWidth((w) => Math.max(20, w - 8))}
            title="Yaxınlaşdırmanı azalt"
            data-testid="gantt-zoom-out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-xs text-zinc-500 w-12 text-center">{dayWidth}px</span>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setDayWidth((w) => Math.min(80, w + 8))}
            title="Yaxınlaşdır"
            data-testid="gantt-zoom-in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="ml-2 h-7"
            onClick={scrollToToday}
            disabled={!showTodayLine}
            data-testid="gantt-today-btn"
          >
            Bu gün
          </Button>
        </div>
      </div>

      {/* Gantt body */}
      <div className="flex">
        {/* Left column: item titles tree */}
        <div
          className="flex-shrink-0 border-r bg-white"
          style={{ width: LEFT_COL_W }}
        >
          {/* Spacer matching header */}
          <div style={{ height: HEADER_H }} className="border-b bg-zinc-50 flex items-end px-3 pb-2">
            <span className="text-xs font-medium text-zinc-600">Mərhələ</span>
          </div>
          {rows.map(({ item, depth, hasChildren, isCollapsed }) => (
            <div
              key={item.id}
              style={{ height: ROW_H, paddingLeft: 12 + depth * 16 }}
              className="border-b flex items-center gap-1.5 px-2 text-xs hover:bg-zinc-50 cursor-pointer"
              onClick={() => handleBarClick(item)}
              data-testid={`gantt-row-${item.id}`}
            >
              {hasChildren ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCollapse(item.id);
                  }}
                  className="h-4 w-4 flex items-center justify-center text-zinc-500 hover:text-zinc-900"
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
              ) : (
                <div className="h-4 w-4" />
              )}
              <div
                className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                style={{ backgroundColor: STATUS_COLORS[item.status] || '#a1a1aa' }}
              />
              <div className="truncate flex-1" title={item.title}>
                {item.title}
              </div>
            </div>
          ))}
        </div>

        {/* Right scroll area: timeline */}
        <div className="flex-1 overflow-x-auto" ref={scrollRef}>
          <div style={{ width: timelineWidth, position: 'relative' }}>
            {/* Header: months + days */}
            <div style={{ height: HEADER_H }} className="border-b bg-zinc-50">
              {/* Month strip */}
              <div className="flex" style={{ height: HEADER_H / 2 }}>
                {monthSpans.map((m) => (
                  <div
                    key={m.key}
                    style={{ width: m.days * dayWidth }}
                    className="border-r border-zinc-200 text-xs font-medium text-zinc-700 px-2 flex items-center"
                  >
                    {m.label}
                  </div>
                ))}
              </div>
              {/* Day strip */}
              <div className="flex" style={{ height: HEADER_H / 2 }}>
                {dayList.map((d, idx) => (
                  <div
                    key={idx}
                    style={{ width: dayWidth }}
                    className={`text-[10px] flex items-center justify-center border-r border-zinc-100 ${
                      isWeekend(d) ? 'bg-zinc-100 text-zinc-500' : 'text-zinc-600'
                    }`}
                  >
                    {d.getDate()}
                  </div>
                ))}
              </div>
            </div>

            {/* Body rows with bars */}
            <div style={{ position: 'relative' }}>
              {rows.map(({ item }, rowIdx) => {
                const startD = item.start_date ? dateOnly(item.start_date) : null;
                const dueD = item.due_date ? dateOnly(item.due_date) : null;
                // If only one date present, use a 1-day bar
                let barStart = startD || dueD;
                let barEnd = dueD || startD;
                if (!barStart || !barEnd) return (
                  <div
                    key={item.id}
                    style={{ height: ROW_H }}
                    className="border-b relative"
                  >
                    {/* Vertical day separators (background pattern) */}
                    <div className="absolute inset-0 flex pointer-events-none">
                      {dayList.map((d, i) => (
                        <div
                          key={i}
                          style={{ width: dayWidth }}
                          className={`border-r border-zinc-100 ${isWeekend(d) ? 'bg-zinc-50' : ''}`}
                        />
                      ))}
                    </div>
                  </div>
                );
                if (barStart > barEnd) [barStart, barEnd] = [barEnd, barStart];
                const offsetDays = diffDays(rangeStart, barStart);
                const lengthDays = Math.max(1, diffDays(barStart, barEnd) + 1);
                const left = offsetDays * dayWidth;
                const width = lengthDays * dayWidth - 4;
                const color = STATUS_COLORS[item.status] || '#a1a1aa';
                const overdue =
                  item.due_date && item.status !== 'done' && new Date(item.due_date) < new Date();
                const assignees = (item.assignee_ids || [])
                  .map((aid) => members.find((m) => m.id === aid))
                  .filter(Boolean)
                  .slice(0, 2);

                return (
                  <div
                    key={item.id}
                    style={{ height: ROW_H }}
                    className="border-b relative"
                  >
                    {/* Day-grid background */}
                    <div className="absolute inset-0 flex pointer-events-none">
                      {dayList.map((d, i) => (
                        <div
                          key={i}
                          style={{ width: dayWidth }}
                          className={`border-r border-zinc-100 ${isWeekend(d) ? 'bg-zinc-50' : ''}`}
                        />
                      ))}
                    </div>
                    {/* Bar */}
                    <div
                      onClick={() => handleBarClick(item)}
                      title={`${item.title} (${STATUS_LABELS[item.status] || item.status})`}
                      data-testid={`gantt-bar-${item.id}`}
                      className="absolute top-1.5 rounded-md cursor-pointer shadow-sm flex items-center px-2 gap-1.5 text-[11px] font-medium text-white overflow-hidden hover:shadow-md transition-shadow"
                      style={{
                        left: left + 2,
                        width: Math.max(width, 12),
                        height: ROW_H - 12,
                        backgroundColor: color,
                        border: overdue ? '1.5px solid #dc2626' : 'none',
                      }}
                    >
                      {overdue && <AlertCircle className="h-3 w-3 flex-shrink-0" />}
                      <span className="truncate">{item.title}</span>
                      {assignees.length > 0 && width > 80 && (
                        <span className="ml-auto flex -space-x-1">
                          {assignees.map((a) => (
                            <span
                              key={a.id}
                              className="h-4 w-4 rounded-full bg-white/30 border border-white/50 text-[8px] font-bold flex items-center justify-center"
                              title={a.full_name || a.username}
                            >
                              {(a.full_name || a.username).slice(0, 1).toUpperCase()}
                            </span>
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Today vertical line */}
              {showTodayLine && (
                <div
                  className="absolute top-0 bottom-0 pointer-events-none"
                  style={{
                    left: todayOffset * dayWidth + dayWidth / 2,
                    width: 2,
                    backgroundColor: '#9333ea',
                  }}
                  data-testid="gantt-today-line"
                >
                  <div className="absolute -top-1 -left-1.5 h-2 w-2 rounded-full bg-purple-600 shadow" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 px-3 py-2 border-t bg-zinc-50 text-[11px] text-zinc-600 flex-wrap">
        {Object.entries(STATUS_LABELS).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: STATUS_COLORS[k] }}
            />
            {v}
          </span>
        ))}
        <Badge variant="outline" className="ml-auto text-[10px] border-purple-300 text-purple-700">
          Bənövşəyi xətt = bu gün
        </Badge>
      </div>

      <ProjectItemDialog
        open={dialogOpen}
        item={editingItem}
        members={members}
        allItems={items}
        canEdit={canEdit}
        onClose={() => {
          setDialogOpen(false);
          setEditingItem(null);
        }}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </div>
  );
}
